import { JSX, useCallback, useContext, useEffect, useState } from 'react';
import { SearchFilterContentTypes, SearchOption, SearchOrigins, SearchUrlParamKeys } from 'search-gateway-client';
import { twMerge } from 'tailwind-merge';

import { Text } from '@videoblocks/design-system';

import {
    NavSearchContext,
    NavSearchContextProvider,
    NavSearchProps,
    StatsigExperimentContext,
    useConversationalSearch,
} from '../context/NavSearchContext';
import { CrossAssetNavSuggestions } from '../types/shared';
import { getSearchWithAIPlaceholder } from '../utils/getSearchWithAIPlaceholder';
import { useMedia } from '../utils/useMedia';
import { saveSearchHistory } from './AutosuggestElements/utils';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import { cleanSearchKeywords, SearchBarVariant } from './NavSearchElements/searchUtils';
import ProminentSearchButton from './ProminentSearchElements/ProminentSearchButton';
import ProminentSearchDropdown from './ProminentSearchElements/ProminentSearchDropdown';
import RightArrowButton from './ProminentSearchElements/RightArrowButton';
import SearchWithAIButton from './ProminentSearchElements/SearchWithAIButton';
import SearchWithAudio from './SearchWithAudioButton';

export interface ProminentSearchProps extends NavSearchProps {
    onDropdownChange: (contentType: string) => void;
    searchTerm: string;
    prominentSearchOptions: SearchOption[];
    contentTypeFromPrevious: SearchFilterContentTypes;
    showDropDown: boolean;
    showContentToggles?: boolean;
    searchGatewayFetch?: boolean;
    warpPage?: boolean;
    aiCtaDisabledForContentType?: boolean;
}

export default function ProminentSearch(props: ProminentSearchProps): JSX.Element {
    const {
        onDropdownChange,
        onSubmit,
        userHasAnySubscription,
        prominentSearchOptions,
        contentTypeFromPrevious,
        showDropDown,
        showContentToggles = false,
        useSnapshots = false,
        searchGatewayFetch = false,
        warpPage = false,
        aiCtaDisabledForContentType = false,
    } = props;

    const defaultSearchOption = prominentSearchOptions?.[0]?.id ?? contentTypeFromPrevious;

    const [contentType, setContentType] = useState(defaultSearchOption ?? contentTypeFromPrevious);
    const [searchTerm, setSearchTerm] = useState(props.searchTerm ?? '');
    const { isSearchHistoryEnabled, showConversationalSearchCta } = useContext(StatsigExperimentContext);
    const { conversationalSearchEnabled } = useConversationalSearch();

    // Ensure searchTerm stays up to date when given a new one via props.
    useEffect(() => {
        setSearchTerm(props.searchTerm ?? '');
    }, [props.searchTerm]);

    useEffect(() => {
        setContentType(contentTypeFromPrevious);
    }, [contentTypeFromPrevious]);

    // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
    const isMediumScreen = useMedia('(min-width: 672px)', true);

    const handleDropdownChange = (contentType: SearchFilterContentTypes) => {
        onDropdownChange(contentType);
        setContentType(contentType);
    };

    const makeEmptySuggestions = (): CrossAssetNavSuggestions => ({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
    });

    const [suggestions, setSuggestions] = useState(makeEmptySuggestions());

    const clearSuggestions = useCallback(() => setSuggestions(makeEmptySuggestions()), []);
    const handleSubmit = (
        contentTypeSearched: SearchFilterContentTypes = contentType,
        value: string = searchTerm,
        searchOriginOverride?: SearchOrigins,
    ) => {
        const cleanedSearchKeywords = cleanSearchKeywords(value);
        const searchOrigin = searchOriginOverride ?? SearchOrigins.SEARCH_BAR;

        if (isSearchHistoryEnabled) {
            saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
        }

        if (conversationalSearchEnabled) {
            const url = new URL('/ai/conv-search/new', window.location.origin);
            const params = new URLSearchParams({
                [SearchUrlParamKeys.SEARCH_ORIGIN]: searchOrigin,
                prompt: cleanedSearchKeywords,
            });
            url.search = params.toString();

            // https://github.com/facebook/react/issues/35158
            // eslint-disable-next-line react-hooks/immutability
            window.location.href = url.toString();
            return;
        }

        onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
    };

    const activeTab = 'bg-white text-gray-800 thick-3 offset-5';
    const inactiveTab = 'text-white bg-black/15';

    return (
        <NavSearchContextProvider {...props}>
            <NavSearchContext.Consumer>
                {({ setShowSuggestions, searchBarVariant }) => (
                    <div
                        className={twMerge(
                            'flex flex-col w-full font-inter text-gray-900 items-center',
                            searchBarVariant === SearchBarVariant.TopNavSearch
                                ? 'rounded-full border border-solid border-gray-400'
                                : '',
                        )}
                    >
                        {showContentToggles && (
                            <div className="product m-0 flex text-base font-bold grid-rows-4 pb-4 md:pb-6 lg:pb-6 cursor-pointer gap-3">
                                {prominentSearchOptions.map((link) => (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        key={link.id}
                                        className={twMerge(
                                            'text-base rounded-sm px-2 py-1 border-0',
                                            contentType === link.id ? activeTab : inactiveTab,
                                        )}
                                        data-value={link.id}
                                        onClick={() => handleDropdownChange(link.id)}
                                        onKeyDown={(event) => event.key === 'Enter' && handleDropdownChange(link.id)}
                                    >
                                        <Text fontWeight="semibold" asChild>
                                            <span>{link.label}</span>
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex items-center justify-center gap-4">
                            <form
                                className={twMerge(
                                    'border-none rounded-full w-full lg:w-[822px] md:w-[480px] flex items-center bg-white',
                                    searchBarVariant === SearchBarVariant.TopNavSearch ? '' : 'pl-5',
                                    showConversationalSearchCta && 'pl-0',
                                )}
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    setShowSuggestions(false);
                                    handleSubmit(contentType, searchTerm);
                                }}
                            >
                                {showDropDown && (
                                    <>
                                        <ProminentSearchDropdown
                                            onChange={handleDropdownChange}
                                            value={contentType}
                                            useSnapshots={useSnapshots}
                                            clearSuggestions={clearSuggestions}
                                            useNewSearchUI={searchBarVariant === SearchBarVariant.TopNavSearch}
                                        />
                                        {searchBarVariant !== SearchBarVariant.TopNavSearch && (
                                            <div>
                                                <div className="w-px h-11 bg-gray-500" />
                                            </div>
                                        )}
                                    </>
                                )}
                                {showConversationalSearchCta && (
                                    <SearchWithAIButton disabledForContentType={aiCtaDisabledForContentType} />
                                )}
                                {isMediumScreen ? (
                                    <CrossAssetNavSearchBox
                                        contentType={contentType}
                                        setSearchTerm={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        suggestions={suggestions}
                                        setSuggestions={setSuggestions}
                                        clearSuggestions={clearSuggestions}
                                        placeholder={getSearchWithAIPlaceholder(
                                            prominentSearchOptions.find(({ id }) => id === contentType)
                                                ?.placeholderText,
                                            contentType,
                                            conversationalSearchEnabled,
                                        )}
                                        searchGatewayFetch={searchGatewayFetch}
                                        isProminentSearch
                                        useWarpSearchUI={warpPage}
                                    />
                                ) : (
                                    <NavSearchBox
                                        contentType={contentType}
                                        onChange={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        placeholder={getSearchWithAIPlaceholder(
                                            prominentSearchOptions.find(({ id }) => id === contentType)
                                                ?.placeholderText,
                                            contentType,
                                            conversationalSearchEnabled,
                                        )}
                                        userHasAnySubscription={userHasAnySubscription}
                                        isProminentSearch
                                        useWarpSearchUI={warpPage}
                                    />
                                )}
                                {showConversationalSearchCta ? <RightArrowButton /> : <ProminentSearchButton />}
                            </form>
                            <SearchWithAudio contentType={contentType} />
                        </div>
                    </div>
                )}
            </NavSearchContext.Consumer>
        </NavSearchContextProvider>
    );
}
